import {AfterViewChecked, Component, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild} from '@angular/core';
import {marked} from 'marked';
import {Subscription} from 'rxjs';
import {AgentChatService} from '../../../_services/agent-chat.service';
import {ChatMessage} from '../../../_models/chat.models';
import {AuthenticationService} from "../../../_services/authentication.service";
import {OrganizzazioniService} from "../../../_services/organizzazioni.service";

@Component({
  selector: 'app-agent-chat',
  templateUrl: './agent-chat.component.html',
  styleUrls: ['./agent-chat.component.scss'],
})
export class AgentChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('scrollArea') scrollArea!: ElementRef<HTMLDivElement>;
  sessionId = signal<string | null>(null);
  messages = signal<ChatMessage[]>([]);
  isLoading = signal(false);
  isThinking = signal(false);
  streamingContent = signal('');
  inputText = '';
  readonly suggestions = [
    'Mostrami gli eventi di produzione di oggi',
    'Movimenti magazzino degli ultimi 7 giorni',
    'Quanti DDT inviati questo mese?',
    'Articoli con invio ERP fallito questa settimana',
  ];
  private readonly chatService = inject(AgentChatService);
  private readonly authService = inject(AuthenticationService);
  private readonly organizzazioneService = inject(OrganizzazioniService);
  // Adatta orgId al tuo contesto (da route param, da AuthService, ecc.)
  private readonly orgId = this.organizzazioneService.organizzazioneId;
  private sub?: Subscription;
  private shouldScroll = false;

  ngOnInit() { this.initSession(); }

  ngOnDestroy() { this.sub?.unsubscribe(); }

  ngAfterViewChecked() {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  newChat() {
    const sid = this.sessionId();
    if (sid) this.chatService.deleteSession(sid).subscribe();
    this.messages.set([]);
    this.streamingContent.set('');
    this.initSession();
  }

  sendSuggestion(text: string) {
    this.inputText = text;
    this.send();
  }

  onEnter(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (!keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      this.send();
    }
  }

  send() {
    const sid = this.sessionId();
    const msg = this.inputText.trim();
    if (!msg || !sid || this.isLoading()) return;

    this.inputText = '';
    this.isLoading.set(true);
    this.isThinking.set(true);
    this.streamingContent.set('');
    this.shouldScroll = true;

    // Aggiungi subito il messaggio utente in UI
    this.messages.update(msgs => [...msgs, {
      id: Date.now().toString(),
      role: 'User',
      type: 'Text',
      content: msg,
      timestamp: new Date().toISOString()
    }]);

    const token = this.authService.currentUserValue.jwtToken;

    this.sub = this.chatService.sendMessage(sid, msg, token).subscribe({
      next: (event) => {
        switch (event.type) {
          case 'status':
            this.isThinking.set(true);
            break;

          case 'chunk':
            this.isThinking.set(false);
            this.streamingContent.update(c => c + event.payload);
            this.shouldScroll = true;
            break;

          case 'history':
            // Sostituisci tutta la lista messaggi con quella aggiornata dal server
            try {
              const history: ChatMessage[] = JSON.parse(event.payload);
              this.messages.set(history);
              this.streamingContent.set('');
            } catch { /* ignora parse error */ }
            break;

          case 'done':
            this.isLoading.set(false);
            this.isThinking.set(false);
            this.shouldScroll = true;
            break;

          case 'error':
            this.isLoading.set(false);
            this.isThinking.set(false);
            break;
        }
      },
      error: () => {
        this.isLoading.set(false);
        this.isThinking.set(false);
      }
    });
  }

  autoResize(el: HTMLTextAreaElement) {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }

  renderMarkdown(text: string): string {
    try { return marked(text) as string; } catch { return text; }
  }

  formatJson(raw: string): string {
    try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
  }

  byteSize(s: string): string {
    const b = new Blob([s]).size;
    return b > 1024 ? `${(b / 1024).toFixed(1)} KB` : `${b} B`;
  }

  private initSession() {
    this.chatService.createSession(this.orgId).subscribe(s => {
      this.sessionId.set(s.sessionId);
    });
  }

  private scrollToBottom() {
    const el = this.scrollArea?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }
}
